<template>
  <!-- AI 智能助手：右下角悬浮入口 + 对话框（SSE 流式） -->
  <div class="ai-chat-dialog">
    <!-- 悬浮球入口（支持拖拽，点击打开） -->
    <div
      ref="floatEl"
      class="ai-float"
      :class="{ 'ai-float-dragging': floatDragging }"
      :style="floatStyle"
      v-show="showComponent"
      @pointerdown="floatDown"
      @pointermove="floatMove"
      @pointerup="floatUp"
      @pointercancel="floatUp"
    >
      <yq-icon class="ai-float-icon" name="kefu"/>
      <span class="ai-float-text">AI 助手</span>
      <span v-if="!chatVisible && unreadTip" class="ai-float-badge"></span>
    </div>

    <!-- 对话面板：从右侧滑入的抽屉 -->
    <el-drawer
      v-model="chatVisible"
      direction="rtl"
      size="480px"
      :append-to-body="true"
      :show-close="false"
      :close-on-press-escape="true"
      class="ai-chat-dialog-panel"
      destroy-on-close
      @opened="onDrawerOpened"
      @closed="onDialogClosed"
    >
      <template #header>
        <div class="ai-dialog-header">
          <div class="ai-dialog-head-left">
            <yq-icon class="ai-dialog-logo" name="kefu"/>
            <div class="ai-dialog-titles">
              <span class="ai-dialog-name">AI 智能助手</span>
              <span class="ai-dialog-sub">我是您的AI助手，还在学习中哦</span>
            </div>
          </div>
          <yq-icon class="ai-dialog-close" name="element-ui_close" @click="chatVisible = false"/>
        </div>
      </template>

      <div class="ai-chat-body">
        <!-- 消息列表（事件委托：点击可查询的单据号） -->
        <div class="ai-msg-list" ref="msgListRef" @click="handleMsgClick">
          <!-- 欢迎语 -->
          <div v-if="messageList.length === 0" class="ai-welcome">
            <div class="ai-welcome-title">你好，我是仓储 AI 助手 👋</div>
            <div class="ai-welcome-tip">你可以这样问我：</div>
            <div
              v-for="(s, i) in suggestList"
              :key="i"
              class="ai-suggest-chip"
              @click="quickAsk(s)"
            >
              {{ s }}
            </div>
          </div>

          <!-- 消息 -->
          <div
            v-for="(msg, idx) in messageList"
            :key="msg.id || idx"
            class="ai-msg"
            :class="msg.role === 'user' ? 'ai-msg-user' : 'ai-msg-assistant'"
          >
            <div class="ai-msg-avatar">
              <!-- 用户消息：有头像显示头像图，否则显示占位图标 -->
              <img
                v-if="msg.role === 'user' && userAvatar"
                class="ai-msg-avatar-img"
                :src="userAvatar"
                alt=""
              />
              <yq-icon
                v-else
                :name="msg.role === 'user' ? 'element-ui_user' : 'kefu'"
              />
            </div>
            <div class="ai-msg-bubble">
              <!-- 打字中或 marked 未就绪：先按纯文本展示（保留换行） -->
              <div v-if="!msg.mdHtml" class="ai-msg-plain">{{ msg.content }}</div>
              <!-- 消息结束：markdown 渲染 -->
              <div v-else class="ai-msg-md" v-html="msg.mdHtml"></div>
              <div v-if="msg.role === 'assistant' && idx === messageList.length - 1 && aiThinking" class="ai-thinking">
                <span class="ai-thinking-dot"></span> AI 思考中…
              </div>
            </div>
          </div>
        </div>

        <!-- 输入区 -->
        <div class="ai-input-area">
          <div class="ai-input-wrap">
            <el-input
              ref="chatInputRef"
              v-model="inputText"
              type="textarea"
              :rows="2"
              resize="none"
              :placeholder="aiSending ? 'AI 正在回答，请稍候…' : '请输入你的问题，回车发送'"
              :disabled="aiSending"
              @keydown.enter.exact.prevent="sendMsg"
            />
          </div>
          <div class="ai-input-ops">
            <span class="ai-input-tip">写操作仅生成入库草稿单，不影响库存</span>
            <el-button
              v-if="aiSending"
              type="primary"
              plain
              size="small"
              class="ai-send-btn"
              @click="abortStream"
            >停止</el-button>
            <el-button
              v-else
              type="primary"
              size="small"
              class="ai-send-btn"
              :disabled="!inputText.trim()"
              @click="sendMsg"
            >发送</el-button>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import util from "@/util/util";
import YqIcon from "@/components/YqIcon.vue";
import {markdownToHtml} from "./markdownUtil";
import imageUtil from "@/util/imageUtil";
import commonApi from "@/api/commonApi";

/**
 * AI 智能助手对话框
 * - 右下角悬浮球入口，全系统任意页面可见（Home.vue 布局内常驻）
 * - 与后端 /ai/deepseek/chat 走 SSE 流式；fetch 直读流，不经过 axios 封装（其不支持流式）
 */
export default {
  name: "AiChatDialog",
  components: {YqIcon},
  data() {
    return {
      chatVisible: false,
      unreadTip: false,
      inputText: "",
      messageList: [],      // {role:'user'|'assistant', content, id}
      aiSending: false,     // 是否正在请求
      aiThinking: false,    // 是否在查数据/等待（无文本增量期间）
      abortController: null,// 当前流式请求的中止句柄
      encounteredError: false, // 本轮是否已收到 error 事件
      msgSeq: 0,            // 本地消息序号（生成唯一 id）
      userInfo: "",
      suggestList: [],
      showComponent: false,
      // 悬浮球拖拽：null=使用默认右下角定位；拖动后用 left/top 覆盖
      floatX: null,
      floatY: null,
      floatDragging: false,
      floatDragState: null, // {sx, sy, startLeft, startTop, moved}
      // AI 请求打开的菜单 uri：等本轮对话完整结束后再跳转，避免中途断开 SSE 连接
      pendingMenuUri: "",
    };
  },
  computed: {
    // 悬浮球定位样式（默认右下角；拖动后以 left/top 定位）
    floatStyle() {
      const s = {};
      if (this.floatX !== null) {
        s.left = this.floatX + "px";
      }
      if (this.floatY !== null) {
        s.top = this.floatY + "px";
      }
      return s;
    },
    // 当前登录用户头像（用于消息气泡）
    userAvatar() {
      if (this.userInfo && this.userInfo.iconUrl) {
        return this.getAvatar(this.userInfo.iconUrl);
      }
      return "";
    },
  },
  unmounted() {
    this.abortCurrent();
  },
  created() {
    util.getUserInfo().then(user => this.userInfo = user)
    // 企业是否开启了AI功能：以智能体配置(AI_AGENT)为准，返回了智能体编号即视为已开启（不校验编号内容）
    commonApi.findSetting("AI_AGENT").then(data => {
      const agent = (data && data.settingValue ? String(data.settingValue) : "").trim();
      if (!agent) {
        return;
      }
      // 按智能体给出对应的示例气泡（智能体编号后端决定，此处仅做展示兜底）
      this.suggestList = this.buildSuggestList(agent);
      this.showComponent = true;
    })
  },
  methods: {
    // 按智能体编号生成示例气泡（最多5个）；未知编号给一组通用提示
    buildSuggestList(agent) {
      const ERP_SUGGEST = [
        "有哪些仓库？",
        "查询某某商品的实时库存",
        "查询本周的出入库统计",
        "查询张三昨天提交的入库单",
        "打开物资档案",
      ];
      const PROJECT_SUGGEST = [
        "XX项目有哪些任务已经延期了？",
        "查询XX项目的迭代进度",
        "张三拜访客户的KPI任务完成进度如何？",
        "查询张三昨天填报的日志",
        "XX项目还需要上传哪些文件？",
      ];
      switch (agent.toUpperCase()) {
        case "PROJECT":
          return PROJECT_SUGGEST;
        case "ERP":
          return ERP_SUGGEST;
        default:
          return ["查询百豪项目有哪些任务", "查询张三昨天的日志", "有哪些仓库？"];
      }
    },
    // ===== 悬浮球拖拽 =====
    floatDown(e) {
      if (e.button !== undefined && e.button !== 0) {
        return;
      }
      const el = this.$refs.floatEl;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const startLeft = (this.floatX !== null) ? this.floatX : rect.left;
      const startTop = (this.floatY !== null) ? this.floatY : rect.top;
      this.floatDragState = {sx: e.clientX, sy: e.clientY, startLeft, startTop, moved: false};
      this.floatDragging = true;
      if (e.preventDefault) {
        e.preventDefault();
      }
      // 捕获指针，移出元素也能持续收到 move/up
      try {
        if (el.setPointerCapture) {
          el.setPointerCapture(e.pointerId);
        }
      } catch (err) {
        // 忽略：个别环境不支持
      }
    },
    floatMove(e) {
      const st = this.floatDragState;
      if (!st || !this.floatDragging) {
        return;
      }
      const dx = e.clientX - st.sx;
      const dy = e.clientY - st.sy;
      // 位移过小视为点击，不做移动
      if (!st.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) {
        return;
      }
      st.moved = true;
      const el = this.$refs.floatEl;
      const w = el ? el.offsetWidth : 60;
      const h = el ? el.offsetHeight : 60;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // 限制在视口内
      this.floatX = Math.max(0, Math.min(vw - w, st.startLeft + dx));
      this.floatY = Math.max(0, Math.min(vh - h, st.startTop + dy));
    },
    floatUp(e) {
      if (!this.floatDragging) {
        return;
      }
      const st = this.floatDragState;
      this.floatDragging = false;
      this.floatDragState = null;
      const el = this.$refs.floatEl;
      try {
        if (el && el.releasePointerCapture && e && e.pointerId !== undefined) {
          el.releasePointerCapture(e.pointerId);
        }
      } catch (err) {
        // 忽略
      }
      // 无位移 = 点击，打开对话框
      if (st && !st.moved) {
        this.openChat();
      }
    },
    // 获取头像的url
    getAvatar(avatar){
      return imageUtil.getFileUrlSingle(avatar);
    },
    // 打开对话框（首次自动给一句引导不强制）
    openChat() {
      this.chatVisible = true;
      this.unreadTip = false;
    },
    onDialogClosed() {
      this.abortCurrent();
    },
    // 抽屉展开动画结束：滚到底 + 聚焦输入框
    onDrawerOpened() {
      if (this.messageList.length > 0) {
        this.scrollToBottom();
      }
      this.focusInput();
    },
    // 快捷提问
    quickAsk(text) {
      this.inputText = text;
      this.sendMsg();
    },
    // 给 markdown 里的单据号加可点击链接（IN/OUT/PD/ZC/DB + 数字；不用 lookbehind 保证浏览器兼容）
    linkBillNos(text) {
      if (!text) {
        return text;
      }
      return text.replace(
        /(^|[^A-Za-z0-9])((?:IN|OUT|PD|ZC|DB)\d{6,})([^A-Za-z0-9]|$)/gi,
        (m, pre, no, post) => `${pre}<a class="billno-link" data-billno="${no}">${no}</a>${post}`
      );
    },
    // 消息列表点击委托：命中单据号链接则按单号查询
    handleMsgClick(ev) {
      const a = ev.target && ev.target.closest ? ev.target.closest("a.billno-link") : null;
      if (!a) {
        return;
      }
      const billNo = a.getAttribute("data-billno");
      if (billNo) {
        this.askBillNo(billNo);
      }
    },
    // 按单据号发起一次查询
    askBillNo(billNo) {
      if (this.aiSending) {
        this.$message.warning("AI 正在回答，请稍候再点击单据号");
        return;
      }
      this.inputText = `请按单据号 ${billNo} 查询这张单据的详细信息。`;
      this.sendMsg();
    },
    // 组装请求头（与 axios.js 拦截器同源，流式 fetch 无法复用拦截器）
    buildHeaders() {
      const header = util.findSessionHeader() || util.findCookieHeader() || {};
      const h = {"Content-Type": "application/json", "Accept-Language": navigator.language || navigator.userLanguage};
      if (header.token) {
        h.token = header.token;
        h.systemCode = header.systemCode;
        h.merchantUuid = header.merchantUuid;
      }
      if (header.grantedToken) {
        h.grantedToken = header.grantedToken;
      }
      return h;
    },
    // 组装多轮历史：仅取当前已完成的消息，并最多保留最近 20 条
    // 后端会把 req.message 作为本轮 user 追加，因此历史须在 push 本轮提问前生成
    buildHistory() {
      const history = [];
      for (const m of this.messageList) {
        if (m.role === "user" || m.role === "assistant") {
          if (m.content && m.content.trim()) {
            history.push({role: m.role, content: m.content});
          }
        }
      }
      // 只带最近 20 条（顺序仍为旧→新），避免上下文过长
      return history.slice(-20);
    },
    // 发送
    async sendMsg() {
      const text = (this.inputText || "").trim();
      if (!text || this.aiSending) {
        return;
      }
      // 先在界面外取历史（此时仅含上一轮对话），再 push 本轮消息
      const history = this.buildHistory();
      this.messageList.push({id: this.nextId(), role: "user", content: text});
      // 预置一条 assistant 消息用于流式追加
      this.messageList.push({id: this.nextId(), role: "assistant", content: ""});
      this.inputText = "";
      await this.requestStream(text, history);
    },
    // 真正发一次 SSE 请求
    async requestStream(userText, history) {
      this.aiSending = true;
      this.aiThinking = true;
      this.abortController = new AbortController();
      const url = process.env.VUE_APP_BASE_URL + "/ai/deepseek/chat";
      let currentMsg = this.messageList[this.messageList.length - 1];
      try {
        const resp = await fetch(url, {
          method: "POST",
          headers: this.buildHeaders(),
          signal: this.abortController.signal,
          body: JSON.stringify({message: userText, history}),
        });
        if (!resp.ok) {
          let errText = "";
          try { errText = await resp.text(); } catch (e) {}
          currentMsg.content = "请求失败(" + resp.status + ")" + (errText ? "：" + errText : "");
          this.$message.error("AI 请求失败(" + resp.status + ")");
          return;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buf = "";
        while (true) {
          const {done, value} = await reader.read();
          if (done) {
            break;
          }
          buf += decoder.decode(value, {stream: true});
          const events = this.parseSSE(buf);
          buf = events.rest;
          for (const ev of events.list) {
            this.handleEvent(ev, currentMsg);
          }
        }
        // 残留 buffer
        if (buf.trim()) {
          const events = this.parseSSE(buf);
          for (const ev of events.list) {
            this.handleEvent(ev, currentMsg);
          }
        }
        // 若一个字都没输出且无错误，给个兜底文案
        if (currentMsg.content === "" && !this.encounteredError) {
          currentMsg.content = "（AI 未返回可显示内容，请重试）";
        }
      } catch (e) {
        if (e.name === "AbortError") {
          if (!currentMsg.content) {
            currentMsg.content = "（已停止）";
          }
        } else {
          currentMsg.content = "网络异常：" + (e.message || e);
          this.$message.error("AI 连接失败，请稍后重试");
        }
      } finally {
        this.aiSending = false;
        this.aiThinking = false;
        this.abortController = null;
        this.encounteredError = false;
        // 流中断/异常时若还没渲染，兜底渲染已收到的内容
        this.finalizeMarkdown(currentMsg);
        this.scrollToBottom();
        // 本轮 SSE 已完整结束：若期间 AI 请求了打开菜单，此刻再统一跳转（避免中途断开连接）
        this.flushPendingMenu();
        // AI 回复结束：自动聚焦输入框，便于直接继续输入
        this.focusInput();
      }
    },
    // 解析 SSE 字节缓冲为事件列表；返回 {list, rest}
    parseSSE(buffer) {
      const list = [];
      const blocks = buffer.split("\n\n");
      const rest = blocks.pop() || "";
      for (const block of blocks) {
        let event = "message";
        let data = "";
        for (const line of block.split("\n")) {
          if (line.startsWith("event:")) {
            event = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            data += line.slice(5).trim();
          }
        }
        if (data) {
          list.push({event, data});
        }
      }
      return {list, rest};
    },
    // 处理单个 SSE 事件：后端统一 event=ai，data 为 JSON {type, content}
    handleEvent(ev, currentMsg) {
      if (ev.event !== "ai" || !ev.data) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(ev.data);
      } catch (e) {
        return;
      }
      const type = payload.type;
      const content = payload.content == null ? "" : payload.content;
      if (type === "text") {
        this.aiThinking = false;
        if (content) {
          currentMsg.content += content;
          this.scrollToBottom();
        }
      } else if (type === "status") {
        // 查数据中：文案已由 thinking 指示器体现
        this.aiThinking = true;
      } else if (type === "error") {
        this.encounteredError = true;
        currentMsg.content += (currentMsg.content ? "\n" : "") + content;
        this.$message.error(content || "AI 服务异常");
        this.finalizeMarkdown(currentMsg);
        this.scrollToBottom();
      } else if (type === "openMenu") {
        // AI 请求打开菜单：先解析并暂存 uri，等本轮 SSE 完整结束后再跳转（避免中途断连）
        this.rememberMenu(content);
      } else if (type === "done") {
        this.aiThinking = false;
        this.finalizeMarkdown(currentMsg);
        this.scrollToBottom();
      }
    },
    // 解析菜单名并暂存待打开 uri（不立即跳转，等本轮结束 flushPendingMenu 统一打开，避免中途断连）
    rememberMenu(menuName) {
      if (!menuName) {
        return;
      }
      const name = String(menuName).trim();
      let raw = util.getSession("menuList");
      if (!raw) {
        this.$message.warning("未获取到菜单，请刷新页面后再试");
        return;
      }
      let menuTree = [];
      try {
        menuTree = JSON.parse(raw);
      } catch (e) {
        menuTree = [];
      }
      // 递归查找叶子节点：moduleName 与菜单名匹配（支持包含/模糊）
      const hit = this.findMenuNode(menuTree, name);
      if (hit && hit.moduleComponentUri) {
        this.pendingMenuUri = hit.moduleComponentUri;
      } else {
        this.$message.warning("未找到名为「" + name + "」的菜单页面");
      }
    },
    // 本轮对话完整结束后调用：存在待打开菜单则 emit 给 Home.addTab 打开并收起抽屉
    flushPendingMenu() {
      if (this.pendingMenuUri) {
        const uri = this.pendingMenuUri;
        this.pendingMenuUri = "";
        this.$emit("ai-open-menu", uri);
        this.chatVisible = false;
      }
    },
    // 在菜单树中按名称匹配节点（叶子优先）
    findMenuNode(list, name) {
      const lower = name.toLowerCase();
      // 先整层看有无直接命中(名称相等或包含)，优先叶子（无 children）
      let leaf = null;
      for (const n of list) {
        const mName = (n.moduleName || "").toLowerCase();
        const matched = mName === lower || mName.indexOf(lower) !== -1 || lower.indexOf(mName) !== -1;
        if (matched) {
          if (n.children && n.children.length) {
            const child = this.findMenuNode(n.children, name);
            if (child) {
              return child;
            }
          } else {
            leaf = n;
          }
        }
      }
      if (leaf) {
        return leaf;
      }
      // 无直接命中叶子，则递归子层找
      for (const n of list) {
        if (n.children && n.children.length) {
          const r = this.findMenuNode(n.children, name);
          if (r) {
            return r;
          }
        }
      }
      return null;
    },
    // 消息结束（done/error/流中断）后，尝试把整段 markdown 渲染成 HTML；
    // marked 未安装/加载失败时保持纯文本展示，不影响使用
    finalizeMarkdown(msg) {
      if (!msg || msg.role !== "assistant" || msg.mdHtml || !msg.content || msg.mdRendering) {
        return;
      }
      msg.mdRendering = true;
      // 先把单据号转成可点击链接，再走 markdown 渲染（点击触发按 billNo 查询）
      const enriched = this.linkBillNos(msg.content);
      markdownToHtml(enriched)
        .then((html) => {
          // 渲染期间消息可能已被清空/替换，需兜底
          if (msg.content !== undefined) {
            msg.mdHtml = html;
          }
        })
        .catch(() => {
          // marked 未安装等：静默保持纯文本
        })
        .finally(() => {
          msg.mdRendering = false;
          this.scrollToBottom();
        });
    },
    abortCurrent() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    },
    abortStream() {
      this.abortCurrent();
    },
    nextId() {
      this.msgSeq += 1;
      return Date.now() + "-" + this.msgSeq;
    },
    // 聚焦对话输入框（便于直接继续输入，无需手动点击）
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.chatInputRef;
        if (input) {
          // element-plus el-input 组件实例自带 focus()
          if (typeof input.focus === "function") {
            input.focus();
          } else if (input.$el) {
            // 兜底：聚焦内部 textarea / input
            const native = input.$el.querySelector("textarea") || input.$el.querySelector("input");
            if (native && typeof native.focus === "function") {
              native.focus();
            }
          }
        }
      });
    },
    scrollToBottom() {
      // 多帧滚动：等到 DOM/布局/异步渲染(如 markdown)都稳定后再滚到底
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.$refs.msgListRef;
          if (el) {
            el.scrollTop = el.scrollHeight;
            // 极少数场景(抽屉刚展开)一帧不够，补第二帧确保到位
            requestAnimationFrame(() => {
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            });
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-chat-dialog {
  // 悬浮球
  .ai-float {
    position: fixed;
    right: 12px;
    bottom: 65px;
    z-index: 3000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    cursor: pointer;
    color: #fff;
    background: linear-gradient(135deg, #79bbff 0%, #2f6bd8 100%);
    box-shadow: 0 6px 18px rgba(64, 158, 255, 0.45);
    overflow: hidden;
    touch-action: none;   // 拖动不被浏览器手势(滚动等)抢占
    user-select: none;
    // 科技感：外圈呼吸光环
    &::before {
      content: "";
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 1px solid rgba(121, 187, 255, 0.7);
      animation: ai-float-ring 2.4s ease-out infinite;
      pointer-events: none;
    }
    // 科技感：流动高光扫过
    &::after {
      content: "";
      position: absolute;
      left: -60%;
      top: -60%;
      width: 60%;
      height: 200%;
      background: linear-gradient(115deg, transparent 20%, rgba(255, 255, 255, 0.55) 50%, transparent 80%);
      transform: rotate(15deg);
      animation: ai-float-shine 3.2s ease-in-out infinite;
      pointer-events: none;
    }
    &:hover {
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 10px 24px rgba(64, 158, 255, 0.6);
    }
    // 拖拽中：禁用位移/缩放动画，避免定位跳动
    &.ai-float-dragging,
    &.ai-float-dragging:hover {
      transform: none;
      cursor: grabbing;
      box-shadow: 0 10px 24px rgba(64, 158, 255, 0.6);
    }

    .ai-float-icon {
      font-size: 24px;
      line-height: 1;
      animation: ai-float-bob 2.6s ease-in-out infinite;
    }
    .ai-float-text {
      font-size: 11px;
      line-height: 1.2;
      margin-top: 2px;
    }
    .ai-float-badge {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #f56c6c;
      border: 2px solid #fff;
      animation: ai-float-pulse 1.6s ease-in-out infinite;
    }
  }
}

// 悬浮球外圈光环扩散
@keyframes ai-float-ring {
  0% { transform: scale(0.9); opacity: 0.9; }
  100% { transform: scale(1.5); opacity: 0; }
}
// 高光扫过
@keyframes ai-float-shine {
  0%, 60% { left: -60%; opacity: 0; }
  70% { opacity: 1; }
  100% { left: 130%; opacity: 0; }
}
// 图标轻微上下浮动
@keyframes ai-float-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

@keyframes ai-float-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.3); }
}

// 抽屉头部（通栏浅蓝由 :deep(.el-drawer__header) 提供，此处只排内部）
.ai-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .ai-dialog-head-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .ai-dialog-logo {
    font-size: 20px;
    color: #2f6bd8;
    margin-right: 8px;
    flex-shrink: 0;
  }
  .ai-dialog-titles {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;

    .ai-dialog-name {
      font-size: 15px;
      font-weight: 600;
      color: #1f4fa0;
      line-height: 1.3;
    }
    .ai-dialog-sub {
      font-size: 11px;
      color: #6f9fe6;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .ai-dialog-close {
    font-size: 16px;
    color: #5b8fd8;
    cursor: pointer;
    border-radius: 50%;
    padding: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.7);
      color: #2f6bd8;
    }
  }
}

.ai-chat-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .ai-msg-list {
    flex: 1;
    overflow-y: auto;
    padding: 4px 2px;
    min-height: 0;

    .ai-welcome {
      padding: 18px 6px;
      text-align: center;

      .ai-welcome-title {
        font-size: 15px;
        color: #303133;
        margin-bottom: 6px;
      }
      .ai-welcome-tip {
        font-size: 12px;
        color: #909399;
        margin-bottom: 12px;
      }
      .ai-suggest-chip {
        display: inline-block;
        margin: 4px 6px;
        padding: 6px 12px;
        font-size: 12px;
        color: #5b9bf0;
        background: #ecf5ff;
        border-radius: 14px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: #5b9bf0;
          color: #fff;
        }
      }
    }

    .ai-msg {
      display: flex;
      margin: 10px 0;

      &.ai-msg-user {
        flex-direction: row-reverse;

        .ai-msg-avatar {
          background: linear-gradient(135deg, #7db9ff 0%, #4f97ee 100%);
        }
        .ai-msg-bubble {
          background: linear-gradient(135deg, #6fb0fa 0%, #4f97ee 100%);
          color: #fff;
          border-radius: 12px 2px 12px 12px;
        }
      }

      &.ai-msg-assistant {
        .ai-msg-avatar {
          background: linear-gradient(135deg, #67c23a 0%, #4e9e2c 100%);
        }
        .ai-msg-bubble {
          background: #f4f4f5;
          color: #303133;
          border-radius: 2px 12px 12px 12px;
        }
      }

      .ai-msg-avatar {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 16px;
        overflow: hidden;

        .ai-msg-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }

      .ai-msg-bubble {
        max-width: 72%;
        padding: 9px 12px;
        font-size: 13px;
        line-height: 1.6;
        word-break: break-word;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        .ai-msg-plain {
          white-space: pre-wrap;
          word-break: break-word;
        }
        .ai-msg-md {
          // markdown 渲染后的正文（含代码块/表格/列表等）
          word-break: break-word;

          :deep(p) {
            margin: 0 0 6px;
            &:last-child { margin-bottom: 0; }
          }
          :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
            margin: 8px 0 4px;
            font-weight: 600;
            line-height: 1.4;
          }
          :deep(h1) { font-size: 17px; }
          :deep(h2) { font-size: 16px; }
          :deep(h3) { font-size: 15px; }
          :deep(h4) { font-size: 14px; }
          :deep(h5), :deep(h6) { font-size: 13px; }

          :deep(ul), :deep(ol) {
            margin: 4px 0;
            padding-left: 18px;
          }
          :deep(li) {
            margin: 2px 0;
          }
          :deep(blockquote) {
            margin: 6px 0;
            padding: 2px 10px;
            border-left: 3px solid #d9ecff;
            color: #606266;
            background: #f7fafd;
          }
          :deep(code) {
            padding: 1px 4px;
            margin: 0 2px;
            border-radius: 3px;
            font-size: 12px;
            font-family: Consolas, Menlo, monospace;
            background: rgba(0, 0, 0, 0.06);
          }
          :deep(pre) {
            margin: 6px 0;
            padding: 8px 10px;
            overflow-x: auto;
            border-radius: 6px;
            background: #282c34;
            color: #e6e6e6;

            code {
              padding: 0;
              background: transparent;
              color: inherit;
              font-size: 12px;
              line-height: 1.5;
            }
          }
          :deep(table) {
            margin: 6px 0;
            border-collapse: collapse;
            font-size: 12px;
            max-width: 100%;
            display: block;
            overflow-x: auto;

            th, td {
              border: 1px solid #d0d7de;
              padding: 3px 8px;
              white-space: nowrap;
            }
            th {
              background: #f0f2f5;
              font-weight: 600;
            }
          }
          :deep(hr) {
            margin: 8px 0;
            border: none;
            border-top: 1px solid #e4e7ed;
          }
          :deep(a) {
            color: #5b9bf0;
            text-decoration: underline;
            word-break: break-all;
          }
          :deep(a.billno-link) {
            cursor: pointer;
            font-weight: 600;
            text-decoration: none;
            border-bottom: 1px dashed #5b9bf0;
            padding: 0 1px;
            transition: all 0.2s ease;
            &:hover {
              color: #2f6bd8;
              background: #e8f3ff;
              border-bottom-color: #2f6bd8;
            }
          }
          :deep(strong) { font-weight: 600; }
          :deep(em) { font-style: italic; }
        }
        .ai-thinking {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #909399;
          margin-top: 2px;

          .ai-thinking-dot {
            width: 6px;
            height: 6px;
            margin-right: 5px;
            border-radius: 50%;
            background: #5b9bf0;
            animation: ai-dot-blink 1s ease-in-out infinite;
          }
        }
      }

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .ai-input-area {
    flex-shrink: 0;
    border-top: 1px solid #ebeef5;
    padding-top: 10px;

    .ai-input-wrap {
      :deep(.el-textarea__inner) {
        font-size: 13px;
      }
    }
    .ai-input-ops {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 8px;

      .ai-input-tip {
        font-size: 12px;
        color: #c0c4cc;
      }
      .ai-send-btn {
        margin-left: 8px;
      }
    }
  }
}

@keyframes ai-dot-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

</style>

<!-- 抽屉定制（非 scoped）：el-drawer 由 append-to-body 挂到 body 下，scoped 无法命中，故用全局唯一 class 限定 -->
<style lang="scss">
.ai-chat-dialog-panel {
  // 头部：浅蓝渐变背景，标题由内部小字呈现
  .el-drawer__header {
    margin: 0;
    padding: 0 20px;
    color: #1f4fa0;
    background: linear-gradient(180deg, #e8f3ff 0%, #d4e8ff 100%);
    border-bottom: 1px solid #cfe2ff;
    height: 60px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }
  // 主体：内边距 + 上下布局撑满
  .el-drawer__body {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}
</style>